import { z } from 'zod';

/**
 * Rollen-Enum — wird via Clerk verwaltet, nicht in der DB gespeichert.
 * Wird für API-Responses und Frontend-Zugriffskontrolle genutzt.
 */
export const UserRoleSchema = z.enum(['admin', 'team', 'member', 'fan']);

/** TypeScript Type für User-Rollen */
export type UserRole = z.infer<typeof UserRoleSchema>;

/**
 * Zod Schema für User — Runtime-Validierung + Type-Inference.
 * Clerk-synced: ID entspricht der Clerk User ID (kein cuid).
 */
export const UserSchema = z.object({
  id: z.string(), // Clerk User ID
  email: z.string().email(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  discordId: z.string().nullable().optional(),
  avatarUrl: z.string().url().nullable().optional(),
  loyaltyScore: z.number().int().default(0),
  isActive: z.boolean().default(true),
  lastSeenAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type User = z.infer<typeof UserSchema>;

/**
 * Schema für User-Erstellung.
 * Mindestfelder: email. Alle anderen optional.
 */
export const CreateUserSchema = UserSchema.pick({
  id: true,
  email: true,
  firstName: true,
  lastName: true,
  displayName: true,
  discordId: true,
  avatarUrl: true,
});

/** TypeScript Type für User-Erstellung */
export type CreateUser = z.infer<typeof CreateUserSchema>;

/**
 * Schema für User-Update.
 * Alle Felder optional — PATCH-Semantik.
 */
export const UpdateUserSchema = UserSchema.pick({
  firstName: true,
  lastName: true,
  displayName: true,
  discordId: true,
  avatarUrl: true,
  isActive: true,
  lastSeenAt: true,
}).partial();

/** TypeScript Type für User-Update */
export type UpdateUser = z.infer<typeof UpdateUserSchema>;

/** Zod Schema für eigenes Profil bearbeiten (nur displayName, avatarUrl, discordId) */
export const UpdateOwnProfileSchema = UserSchema.pick({
  displayName: true,
  avatarUrl: true,
  discordId: true,
}).partial();

/** TypeScript Type für eigenes Profil-Update */
export type UpdateOwnProfile = z.infer<typeof UpdateOwnProfileSchema>;

/**
 * Schema für das Ändern des Aktivierungsstatus eines Users.
 * Wird für PATCH /persons/:id/status genutzt.
 */
export const ChangeStatusSchema = z.object({
  isActive: z.boolean(),
});

/** TypeScript Type für Status-Änderung */
export type ChangeStatus = z.infer<typeof ChangeStatusSchema>;

/**
 * Schema für das Anlegen einer User-Sperre.
 * Wird für POST /persons/:id/block genutzt.
 */
export const CreateUserBlockSchema = z.object({
  /** Pflicht-Begründung der Sperre */
  reason: z.string().min(1, 'Grund ist erforderlich'),
  /** Ablaufdatum (ISO-String). Kein Wert = permanente Sperre. */
  endsAt: z.string().datetime().optional(),
});

/** TypeScript Type für User-Sperre-Erstellung */
export type CreateUserBlock = z.infer<typeof CreateUserBlockSchema>;

/**
 * Schema für Admin-seitiges Profil-Update eines Fans.
 * Erlaubt Änderung von Name, DisplayName und E-Mail.
 * Wird für PATCH /persons/:id genutzt.
 */
export const UpdatePersonSchema = z.object({
  /** Vorname des Fans */
  firstName: z.string().min(1, 'Vorname darf nicht leer sein').optional(),
  /** Nachname des Fans */
  lastName: z.string().optional(),
  /** Anzeigename des Fans */
  displayName: z.string().optional(),
  /** E-Mail-Adresse (wird auf Duplikate geprüft) */
  email: z.string().email('Ungültige E-Mail-Adresse').optional(),
});

/** TypeScript Type für Admin-Profil-Update */
export type UpdatePerson = z.infer<typeof UpdatePersonSchema>;

/**
 * Schema für das Aktualisieren der internen Notizen eines Fans.
 * Wird für PATCH /persons/:id/notes genutzt.
 */
export const UpdatePersonNotesSchema = z.object({
  /** Interne Anmerkungen zum Fan — null löscht die Notiz */
  notes: z.string().nullable(),
});

/** TypeScript Type für Notizen-Update eines Fans */
export type UpdatePersonNotes = z.infer<typeof UpdatePersonNotesSchema>;

/**
 * Schema für den Merge zweier Fan-Profile.
 * sourceId wird in targetId aufgelöst — alle FK-Referenzen werden übertragen.
 */
export const MergePersonsSchema = z
  .object({
    /** ID des Quell-Fans (wird nach Merge gelöscht) */
    sourceId: z.string().min(1, 'Quell-ID ist erforderlich'),
    /** ID des Ziel-Fans (bleibt erhalten) */
    targetId: z.string().min(1, 'Ziel-ID ist erforderlich'),
  })
  .refine((d) => d.sourceId !== d.targetId, {
    message: 'Quell- und Ziel-Fan dürfen nicht identisch sein',
  });

/** TypeScript Type für Fan-Merge */
export type MergePersons = z.infer<typeof MergePersonsSchema>;

/**
 * Minimales User-Profil für Merge-Vorschau und Suche.
 */
export const PersonSummarySchema = UserSchema.pick({
  id: true,
  firstName: true,
  lastName: true,
  email: true,
  displayName: true,
});

/** TypeScript Type für Person-Kurzinfo */
export type PersonSummary = z.infer<typeof PersonSummarySchema>;

/**
 * Response-Schema für die Merge-Vorschau.
 * Liefert Quell- und Ziel-Fan sowie Anzahl zu migrierender Records.
 */
export const MergePreviewResponseSchema = z.object({
  source: PersonSummarySchema,
  target: PersonSummarySchema,
  counts: z.object({
    tickets: z.number(),
    checkIns: z.number(),
    registrations: z.number(),
    reservations: z.number(),
    loyaltyEvents: z.number(),
    blocks: z.number(),
    waitlist: z.number(),
    mailLogs: z.number(),
    auditLogs: z.number(),
    mediaFiles: z.number(),
  }),
});

/** TypeScript Type für Merge-Vorschau-Response */
export type MergePreviewResponse = z.infer<typeof MergePreviewResponseSchema>;

/**
 * Response-Schema für den Lösch-Preflight-Check.
 * Gibt an ob ein Fan gelöscht werden kann und wie viele Records betroffen sind.
 */
export const DeletePreflightResponseSchema = z.object({
  /** Ob der Fan gelöscht werden kann (false wenn zugewiesene Tickets vorhanden) */
  canDelete: z.boolean(),
  /** Begründung wenn canDelete false ist */
  reason: z.string().optional(),
  counts: z.object({
    tickets: z.number(),
    assignedTickets: z.number(),
    checkIns: z.number(),
    registrations: z.number(),
    reservations: z.number(),
    loyaltyEvents: z.number(),
    blocks: z.number(),
    waitlist: z.number(),
    mailLogs: z.number(),
    auditLogs: z.number(),
    mediaFiles: z.number(),
  }),
});

/** TypeScript Type für Delete-Preflight-Response */
export type DeletePreflightResponse = z.infer<typeof DeletePreflightResponseSchema>;
